import { useCallback, useEffect, useState } from "react";
import { Buildings, Sparkle, QrCode, Package, ArrowsLeftRight, Storefront, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BinScanner } from "@/components/BinScanner";

const err = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

const fillTone = (pct) => {
  if (pct == null) return "border-border bg-white text-slate-400";
  if (pct === 0) return "border-dashed border-slate-300 bg-slate-50 text-slate-400";
  if (pct >= 95) return "border-[#B3241B] bg-[#FFECEB] text-[#7A1712]";
  if (pct >= 60) return "border-[#B47500] bg-[#FFF6E5] text-[#7A5200]";
  return "border-[#0F7A3D] bg-[#EAF7EF] text-[#0B5C2E]";
};

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-border bg-white p-4">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-2 text-2xl font-bold text-[#0F172A]">{value}</p>
  </div>
);

export default function Warehouse() {
  const { user } = useAuth();
  const isManager = ["admin", "vendor"].includes(user?.role);
  const [view, setView] = useState("map");
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [map, setMap] = useState(null);
  const [items, setItems] = useState([]);
  const [stock, setStock] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [virtual, setVirtual] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [openBin, setOpenBin] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const [siteForm, setSiteForm] = useState({ name: "", kind: "own", city: "", country: "US", capacity_pallets: 0 });
  const [zoneForm, setZoneForm] = useState({ zone: "", rows: 2, columns: 4, capacity_units: 100, kind: "rack" });
  const [putForm, setPutForm] = useState({ item_id: "", bin_code: "", quantity: 1 });
  const [moveForm, setMoveForm] = useState({ item_id: "", from_bin_code: "", to_bin_code: "", quantity: 1 });
  const [transferForm, setTransferForm] = useState({ from_site_id: "", to_site_id: "", item_id: "", quantity: 1 });
  const [virtualForm, setVirtualForm] = useState({ supplier_name: "", item_name: "", quantity_available: 0, unit: "pcs", unit_price: 0, currency: "usd", lead_time_days: 3, dropship: true, country: "US" });
  const [spaceForm, setSpaceForm] = useState({ title: "", space_type: "pallet", city: "", country: "US", pallets_available: 0, sqm_available: 0, rate_per_pallet_month: 0, rate_per_sqm_month: 0, currency: "usd", min_months: 1 });
  const [taskForm, setTaskForm] = useState({ kind: "pick", item_id: "", quantity: 1, assignee_id: "", reference_note: "" });
  const [people, setPeople] = useState([]);

  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get("/warehouse/sites");
      setSites(data);
      setSiteId((cur) => cur || data[0]?.id || "");
    } catch { setSites([]); }
  }, []);

  const loadMap = useCallback(async () => {
    if (!siteId) { setMap(null); return; }
    try {
      const { data } = await api.get(`/warehouse/sites/${siteId}/map`);
      setMap(data);
    } catch { setMap(null); }
  }, [siteId]);

  useEffect(() => { loadSites(); }, [loadSites]);
  useEffect(() => { loadMap(); }, [loadMap]);
  useEffect(() => {
    api.get("/items").then(({ data }) => setItems(data)).catch(() => setItems([]));
    if (isManager) api.get("/users").then(({ data }) => setPeople(data)).catch(() => setPeople([]));
  }, [isManager]);

  const loadStock = useCallback(() => api.get("/warehouse/stock").then(({ data }) => setStock(data)).catch(() => setStock(null)), []);
  const loadTransfers = useCallback(() => api.get("/warehouse/transfers").then(({ data }) => setTransfers(data)).catch(() => setTransfers([])), []);
  const loadVirtual = useCallback(() => api.get("/warehouse/virtual").then(({ data }) => setVirtual(data)).catch(() => setVirtual([])), []);
  const loadSpaces = useCallback(() => {
    api.get("/warehouse/space").then(({ data }) => setSpaces(data)).catch(() => setSpaces([]));
    api.get("/warehouse/space/bookings").then(({ data }) => setBookings(data)).catch(() => setBookings([]));
  }, []);
  const loadTasks = useCallback(() => api.get("/warehouse/tasks").then(({ data }) => setTasks(data)).catch(() => setTasks([])), []);

  useEffect(() => {
    if (view === "stock") loadStock();
    if (view === "transfers") loadTransfers();
    if (view === "virtual") loadVirtual();
    if (view === "space") loadSpaces();
    if (view === "tasks") loadTasks();
  }, [view, loadStock, loadTransfers, loadVirtual, loadSpaces, loadTasks]);

  const addSite = async () => {
    try {
      await api.post("/warehouse/sites", { ...siteForm, capacity_pallets: Number(siteForm.capacity_pallets) });
      toast.success("Warehouse added");
      setSiteForm({ ...siteForm, name: "" });
      loadSites();
    } catch (e) { err(e); }
  };

  const addZone = async () => {
    try {
      const { data } = await api.post(`/warehouse/sites/${siteId}/zones`, {
        ...zoneForm, rows: Number(zoneForm.rows), columns: Number(zoneForm.columns),
        capacity_units: Number(zoneForm.capacity_units),
      });
      toast.success(`Zone ${data.zone} laid out with ${data.bins_created} bins`);
      setZoneForm({ ...zoneForm, zone: "" });
      loadMap();
    } catch (e) { err(e); }
  };

  const doPutaway = async () => {
    try {
      await api.post("/warehouse/putaway", { ...putForm, site_id: siteId, quantity: Number(putForm.quantity) });
      toast.success("Stock put away");
      loadMap();
      loadSites();
    } catch (e) { err(e); }
  };

  const doMove = async () => {
    try {
      const { data } = await api.post("/warehouse/move", { ...moveForm, site_id: siteId, quantity: Number(moveForm.quantity) });
      toast.success(`Moved ${data.from} → ${data.to}`);
      setScanOpen(false);
      loadMap();
      loadSites();
    } catch (e) { err(e); }
  };

  const askPutawayAi = async () => {
    if (!putForm.item_id) { toast.error("Pick the item first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/warehouse/ai/putaway", { site_id: siteId, item_id: putForm.item_id, quantity: Number(putForm.quantity) });
      toast.success(data.advice);
      if (data.bin_code) setPutForm((f) => ({ ...f, bin_code: data.bin_code }));
    } catch (e) { err(e); } finally { setAiBusy(false); }
  };

  const runSlotting = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/warehouse/ai/slotting", { site_id: siteId });
      setAiReview(data.review);
    } catch (e) { err(e); } finally { setAiBusy(false); }
  };

  const raiseTransfer = async () => {
    try {
      await api.post("/warehouse/transfers", {
        from_site_id: transferForm.from_site_id, to_site_id: transferForm.to_site_id,
        lines: [{ item_id: transferForm.item_id, quantity: Number(transferForm.quantity) }],
      });
      toast.success("Transfer raised");
      loadTransfers();
    } catch (e) { err(e); }
  };

  const moveTransfer = async (id, status) => {
    let to_bin_code = null;
    if (status === "received") {
      to_bin_code = window.prompt("Scan or type the bin the stock goes into");
      if (!to_bin_code) return;
    }
    try {
      await api.post(`/warehouse/transfers/${id}/status`, { status, to_bin_code });
      toast.success(`Transfer ${status.replace("_", " ")}`);
      loadTransfers();
      loadMap();
      loadSites();
    } catch (e) { err(e); }
  };

  const addVirtual = async () => {
    try {
      await api.post("/warehouse/virtual", {
        ...virtualForm,
        quantity_available: Number(virtualForm.quantity_available),
        unit_price: Number(virtualForm.unit_price),
        lead_time_days: Number(virtualForm.lead_time_days),
      });
      toast.success("Third-party stock listed");
      setVirtualForm({ ...virtualForm, item_name: "" });
      loadVirtual();
    } catch (e) { err(e); }
  };

  const orderVirtual = async (row) => {
    const qty = window.prompt(`How many ${row.unit} of ${row.item_name}?`, "1");
    if (!qty) return;
    try {
      await api.post(`/warehouse/virtual/${row.id}/order`, { quantity: Number(qty) });
      toast.success("Order sent to the supplier");
      loadVirtual();
    } catch (e) { err(e); }
  };

  const addSpace = async () => {
    try {
      await api.post("/warehouse/space", {
        ...spaceForm,
        pallets_available: Number(spaceForm.pallets_available),
        sqm_available: Number(spaceForm.sqm_available),
        rate_per_pallet_month: Number(spaceForm.rate_per_pallet_month),
        rate_per_sqm_month: Number(spaceForm.rate_per_sqm_month),
        min_months: Number(spaceForm.min_months),
      });
      toast.success("Space listed");
      setSpaceForm({ ...spaceForm, title: "" });
      loadSpaces();
    } catch (e) { err(e); }
  };

  const bookSpace = async (row) => {
    const pallets = window.prompt("How many pallet spaces?", "1");
    if (!pallets) return;
    const months = window.prompt("For how many months?", String(row.min_months || 1));
    if (!months) return;
    try {
      const { data } = await api.post(`/warehouse/space/${row.id}/book`, { pallets: Number(pallets), months: Number(months) });
      toast.success(`Requested — ${String(data.currency).toUpperCase()} ${data.total_cost.toLocaleString()} total`);
      loadSpaces();
    } catch (e) { err(e); }
  };

  const answerBooking = async (id, status) => {
    try {
      await api.post(`/warehouse/space/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      loadSpaces();
    } catch (e) { err(e); }
  };

  const addTask = async () => {
    try {
      await api.post("/warehouse/tasks", {
        kind: taskForm.kind, site_id: siteId,
        assignee_id: taskForm.assignee_id || null,
        reference_note: taskForm.reference_note || null,
        lines: [{ item_id: taskForm.item_id, quantity: Number(taskForm.quantity) }],
      });
      toast.success("Task raised");
      loadTasks();
    } catch (e) { err(e); }
  };

  const tickLine = async (task, index, done) => {
    try {
      await api.post(`/warehouse/tasks/${task.id}/line`, { line_index: index, done });
      loadTasks();
    } catch (e) { err(e); }
  };

  const totalBins = map?.zones?.reduce((n, z) => n + z.bins.length, 0) || 0;
  const usedBins = map?.zones?.reduce((n, z) => n + z.bins.filter((b) => b.used_units > 0).length, 0) || 0;

  return (
    <div className="space-y-6" data-testid="warehouse-page">
      <div>
        <p className="overline text-[#B3241B]">Virtual warehouse</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F172A]">Every bin, every site, on one screen</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          A digital twin of your stores — zones, racks and bins with what's in them — plus stock across
          several sites, transfers, third-party stock you don't own, spare space to rent out, pick lists
          for the floor and AI slotting advice.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Warehouses" value={sites.length} testid="wh-stat-sites" />
        <Stat label="Bins here" value={totalBins} testid="wh-stat-bins" />
        <Stat label="Bins in use" value={usedBins} testid="wh-stat-used" />
        <Stat label="Units held here" value={sites.find((s) => s.id === siteId)?.units_held ?? 0} testid="wh-stat-units" />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Warehouse</Label>
          <select data-testid="wh-site-select" className="mt-1 border border-border bg-white px-3 py-2 text-base md:text-sm" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">Pick a warehouse</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{`${s.name} · ${s.city || s.country}`}</option>)}
          </select>
        </div>
        <Button variant="outline" data-testid="wh-scan-open" onClick={() => setScanOpen(true)}>
          <QrCode size={16} className="mr-1.5" /> Scan to move stock
        </Button>
        {isManager && siteId && (
          <Button variant="outline" data-testid="wh-ai-slotting" onClick={runSlotting} disabled={aiBusy}>
            <Sparkle size={16} className="mr-1.5" /> {aiBusy ? "Thinking…" : "AI slotting review"}
          </Button>
        )}
      </div>

      {aiReview && (
        <div data-testid="wh-ai-review" className="whitespace-pre-line border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3 text-sm text-[#0F172A]">
          {aiReview}
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="w-full scroll-x overflow-x-auto">
        <TabsList data-testid="wh-tabs" className="w-max">
          <TabsTrigger value="map" data-testid="wh-tab-map">Floor map</TabsTrigger>
          <TabsTrigger value="stock" data-testid="wh-tab-stock">Stock by site</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="wh-tab-transfers">Transfers</TabsTrigger>
          <TabsTrigger value="virtual" data-testid="wh-tab-virtual">Third-party stock</TabsTrigger>
          <TabsTrigger value="space" data-testid="wh-tab-space">Space market</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="wh-tab-tasks">Pick &amp; put-away</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "map" && (
        <div className="space-y-4" data-testid="wh-map">
          {isManager && (
            <div className="grid gap-3 lg:grid-cols-2">
              <div className="border border-border bg-white p-4">
                <p className="text-sm font-semibold text-[#0F172A]">Add a warehouse</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div><Label>Name</Label><Input data-testid="wh-site-name" value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="Main stores" /></div>
                  <div>
                    <Label>Kind</Label>
                    <select data-testid="wh-site-kind" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={siteForm.kind} onChange={(e) => setSiteForm({ ...siteForm, kind: e.target.value })}>
                      <option value="own">Our own</option>
                      <option value="partner">Partner / 3PL</option>
                      <option value="virtual">Virtual (supplier held)</option>
                    </select>
                  </div>
                  <div><Label>City</Label><Input data-testid="wh-site-city" value={siteForm.city} onChange={(e) => setSiteForm({ ...siteForm, city: e.target.value })} /></div>
                  <div><Label>Country</Label><Input data-testid="wh-site-country" value={siteForm.country} onChange={(e) => setSiteForm({ ...siteForm, country: e.target.value.toUpperCase() })} /></div>
                </div>
                <Button className="mt-3" data-testid="wh-site-submit" onClick={addSite}>Add the warehouse</Button>
              </div>
              <div className="border border-border bg-white p-4">
                <p className="text-sm font-semibold text-[#0F172A]">Lay out a zone</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div><Label>Zone</Label><Input data-testid="wh-zone-name" value={zoneForm.zone} onChange={(e) => setZoneForm({ ...zoneForm, zone: e.target.value })} placeholder="A" /></div>
                  <div><Label>Rows</Label><Input data-testid="wh-zone-rows" type="number" min={1} value={zoneForm.rows} onChange={(e) => setZoneForm({ ...zoneForm, rows: e.target.value })} /></div>
                  <div><Label>Columns</Label><Input data-testid="wh-zone-cols" type="number" min={1} value={zoneForm.columns} onChange={(e) => setZoneForm({ ...zoneForm, columns: e.target.value })} /></div>
                  <div><Label>Bin holds</Label><Input data-testid="wh-zone-capacity" type="number" min={1} value={zoneForm.capacity_units} onChange={(e) => setZoneForm({ ...zoneForm, capacity_units: e.target.value })} /></div>
                </div>
                <Button className="mt-3" data-testid="wh-zone-submit" onClick={addZone} disabled={!siteId}>Create the bins</Button>
              </div>
            </div>
          )}

          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">Put stock away</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label>Item</Label>
                <select data-testid="wh-put-item" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={putForm.item_id} onChange={(e) => setPutForm({ ...putForm, item_id: e.target.value })}>
                  <option value="">Pick an item</option>
                  {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
              <div><Label>Bin code</Label><Input data-testid="wh-put-bin" value={putForm.bin_code} onChange={(e) => setPutForm({ ...putForm, bin_code: e.target.value.toUpperCase() })} placeholder="A-01-02" /></div>
              <div><Label>Quantity</Label><Input data-testid="wh-put-qty" type="number" min={0} value={putForm.quantity} onChange={(e) => setPutForm({ ...putForm, quantity: e.target.value })} /></div>
              <div className="flex items-end gap-2">
                <Button data-testid="wh-put-submit" onClick={doPutaway}>Put away</Button>
                <Button variant="outline" data-testid="wh-put-ai" onClick={askPutawayAi} disabled={aiBusy}><Sparkle size={15} className="mr-1" /> Best bin</Button>
              </div>
            </div>
          </div>

          {!map ? (
            <p data-testid="wh-map-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
              <Buildings size={20} className="mx-auto mb-2 text-slate-400" /> Add a warehouse and lay out a zone to see the floor map.
            </p>
          ) : map.zones.length === 0 ? (
            <p data-testid="wh-no-zones" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No zones here yet.</p>
          ) : (
            map.zones.map((zone) => (
              <div key={zone.zone} className="border border-border bg-white p-4" data-testid={`wh-zone-${zone.zone}`}>
                <p className="text-sm font-semibold text-[#0F172A]">Zone {zone.zone} <span className="text-xs font-normal text-slate-500">· {zone.kind} · {zone.bins.length} bins</span></p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {zone.bins.map((b) => (
                    <button key={b.id} data-testid={`wh-bin-${b.code}`} onClick={() => setOpenBin(b)}
                      className={`min-w-[86px] border px-2 py-2 text-left text-[11px] font-semibold transition-colors duration-150 hover:border-[#002FA7] ${fillTone(b.fill_pct)}`}>
                      <span className="block">{b.code}</span>
                      <span className="block font-normal">{b.used_units ? `${b.used_units}/${b.capacity_units}` : "empty"}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {view === "stock" && (
        <div className="space-y-4" data-testid="wh-stock">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(stock?.sites || []).map((s, i) => (
              <div key={s.site_name} data-testid={`wh-site-card-${i}`} className="border border-border bg-white p-4">
                <p className="font-semibold text-[#0F172A]">{s.site_name}</p>
                <p className="mt-1 text-sm text-slate-600">{s.units} units · {s.item_count} item(s)</p>
              </div>
            ))}
          </div>
          {!stock || stock.items.length === 0 ? (
            <p data-testid="wh-stock-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No stock has been put away yet.</p>
          ) : (
            <>
            <div className="space-y-2 md:hidden" data-testid="wh-stock-cards">
              {stock.items.map((row, i) => (
                <div key={row.item_id} data-testid={`wh-stock-card-${i}`} className="border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-[#0F172A]">{row.item_name}</p>
                    <span className="text-sm font-semibold text-[#0F172A]">{row.total} {row.unit}</span>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    {row.locations.map((l) => `${l.site_name} ${l.bin_code} (${l.quantity})`).join(" · ")}
                  </p>
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto border border-border bg-white md:block">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="p-3 text-left">Item</th><th className="p-3 text-right">Total</th><th className="p-3 text-left">Where it is</th></tr>
                </thead>
                <tbody data-testid="wh-stock-rows">
                  {stock.items.map((row, i) => (
                    <tr key={row.item_id} className="border-t border-border" data-testid={`wh-stock-row-${i}`}>
                      <td className="p-3 font-semibold text-[#0F172A]">{row.item_name}</td>
                      <td className="p-3 text-right">{row.total} {row.unit}</td>
                      <td className="p-3 text-slate-600">
                        {row.locations.map((l) => `${l.site_name} ${l.bin_code} (${l.quantity})`).join(" · ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      {view === "transfers" && (
        <div className="space-y-4" data-testid="wh-transfers">
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">Move stock between warehouses</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label>From</Label>
                <select data-testid="wh-transfer-from" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={transferForm.from_site_id} onChange={(e) => setTransferForm({ ...transferForm, from_site_id: e.target.value })}>
                  <option value="">Pick a warehouse</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label>To</Label>
                <select data-testid="wh-transfer-to" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={transferForm.to_site_id} onChange={(e) => setTransferForm({ ...transferForm, to_site_id: e.target.value })}>
                  <option value="">Pick a warehouse</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Item</Label>
                <select data-testid="wh-transfer-item" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={transferForm.item_id} onChange={(e) => setTransferForm({ ...transferForm, item_id: e.target.value })}>
                  <option value="">Pick an item</option>
                  {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
              <div><Label>Quantity</Label><Input data-testid="wh-transfer-qty" type="number" min={0} value={transferForm.quantity} onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })} /></div>
            </div>
            <Button className="mt-3" data-testid="wh-transfer-submit" onClick={raiseTransfer}><ArrowsLeftRight size={16} className="mr-1.5" /> Raise the transfer</Button>
          </div>
          {transfers.length === 0 ? (
            <p data-testid="wh-transfers-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No transfers raised yet.</p>
          ) : transfers.map((t, i) => (
            <div key={t.id} data-testid={`wh-transfer-${i}`} className="border border-border bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-[#0F172A]">{t.reference} · {t.from_site_name} → {t.to_site_name}</p>
                <span className="border border-border px-2 py-1 text-xs font-semibold text-slate-600">{t.status.replace(/_/g, " ")}</span>
              </div>
              <p className="mt-2 text-sm text-slate-600">{t.lines.map((l) => `${l.item_name} × ${l.quantity} ${l.unit}`).join(", ")}</p>
              {isManager && !["received", "cancelled"].includes(t.status) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {t.status === "requested" && <Button size="sm" variant="outline" data-testid={`wh-transfer-dispatch-${i}`} onClick={() => moveTransfer(t.id, "in_transit")}>Mark on the road</Button>}
                  <Button size="sm" data-testid={`wh-transfer-receive-${i}`} onClick={() => moveTransfer(t.id, "received")}>Receive into a bin</Button>
                  <Button size="sm" variant="outline" data-testid={`wh-transfer-cancel-${i}`} onClick={() => moveTransfer(t.id, "cancelled")}>Cancel</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {view === "virtual" && (
        <div className="space-y-4" data-testid="wh-virtual">
          {isManager && (
            <div className="border border-border bg-white p-4">
              <p className="text-sm font-semibold text-[#0F172A]">List stock you don't hold yourself</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div><Label>Supplier</Label><Input data-testid="wh-virtual-supplier" value={virtualForm.supplier_name} onChange={(e) => setVirtualForm({ ...virtualForm, supplier_name: e.target.value })} /></div>
                <div><Label>Item</Label><Input data-testid="wh-virtual-item" value={virtualForm.item_name} onChange={(e) => setVirtualForm({ ...virtualForm, item_name: e.target.value })} /></div>
                <div><Label>Available</Label><Input data-testid="wh-virtual-qty" type="number" min={0} value={virtualForm.quantity_available} onChange={(e) => setVirtualForm({ ...virtualForm, quantity_available: e.target.value })} /></div>
                <div><Label>Price each</Label><Input data-testid="wh-virtual-price" type="number" min={0} value={virtualForm.unit_price} onChange={(e) => setVirtualForm({ ...virtualForm, unit_price: e.target.value })} /></div>
                <div><Label>Lead time (days)</Label><Input data-testid="wh-virtual-lead" type="number" min={0} value={virtualForm.lead_time_days} onChange={(e) => setVirtualForm({ ...virtualForm, lead_time_days: e.target.value })} /></div>
                <div><Label>Country</Label><Input data-testid="wh-virtual-country" value={virtualForm.country} onChange={(e) => setVirtualForm({ ...virtualForm, country: e.target.value.toUpperCase() })} /></div>
                <label className="flex items-end gap-2 pb-2 text-sm text-slate-600">
                  <input data-testid="wh-virtual-dropship" type="checkbox" checked={virtualForm.dropship} onChange={(e) => setVirtualForm({ ...virtualForm, dropship: e.target.checked })} />
                  Drop-ships direct
                </label>
              </div>
              <Button className="mt-3" data-testid="wh-virtual-submit" onClick={addVirtual}>List it</Button>
            </div>
          )}
          {virtual.length === 0 ? (
            <p data-testid="wh-virtual-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
              <Package size={20} className="mx-auto mb-2 text-slate-400" /> No third-party stock listed yet.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wh-virtual-list">
              {virtual.map((row, i) => (
                <div key={row.id} data-testid={`wh-virtual-${i}`} className="border border-border bg-white p-4">
                  <p className="font-semibold text-[#0F172A]">{row.item_name}</p>
                  <p className="mt-1 text-xs text-slate-500">{row.supplier_name} · {row.held_at || row.country}</p>
                  <p className="mt-2 text-sm text-slate-600">
                    {row.quantity_available} {row.unit} · {String(row.currency).toUpperCase()} {row.unit_price} each · {row.lead_time_days} day lead
                    {row.dropship ? " · drop-ships" : ""}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" data-testid={`wh-virtual-order-${i}`} onClick={() => orderVirtual(row)}>Order</Button>
                    {(user?.role === "admin" || row.owner_id === user?.id) && (
                      <Button size="sm" variant="outline" data-testid={`wh-virtual-withdraw-${i}`} onClick={async () => {
                        try { await api.delete(`/warehouse/virtual/${row.id}`); loadVirtual(); toast.success("Withdrawn"); } catch (e) { err(e); }
                      }}>Withdraw</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "space" && (
        <div className="space-y-4" data-testid="wh-space">
          {isManager && (
            <div className="border border-border bg-white p-4">
              <p className="text-sm font-semibold text-[#0F172A]">Rent out your spare space</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div><Label>Title</Label><Input data-testid="wh-space-title" value={spaceForm.title} onChange={(e) => setSpaceForm({ ...spaceForm, title: e.target.value })} placeholder="Dry pallet space, Pune" /></div>
                <div>
                  <Label>Type</Label>
                  <select data-testid="wh-space-type" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={spaceForm.space_type} onChange={(e) => setSpaceForm({ ...spaceForm, space_type: e.target.value })}>
                    {["pallet", "sqm", "shelf", "cold", "yard", "hazmat"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><Label>City</Label><Input data-testid="wh-space-city" value={spaceForm.city} onChange={(e) => setSpaceForm({ ...spaceForm, city: e.target.value })} /></div>
                <div><Label>Country</Label><Input data-testid="wh-space-country" value={spaceForm.country} onChange={(e) => setSpaceForm({ ...spaceForm, country: e.target.value.toUpperCase() })} /></div>
                <div><Label>Pallets free</Label><Input data-testid="wh-space-pallets" type="number" min={0} value={spaceForm.pallets_available} onChange={(e) => setSpaceForm({ ...spaceForm, pallets_available: e.target.value })} /></div>
                <div><Label>Rate per pallet / month</Label><Input data-testid="wh-space-rate" type="number" min={0} value={spaceForm.rate_per_pallet_month} onChange={(e) => setSpaceForm({ ...spaceForm, rate_per_pallet_month: e.target.value })} /></div>
                <div><Label>Sqm free</Label><Input data-testid="wh-space-sqm" type="number" min={0} value={spaceForm.sqm_available} onChange={(e) => setSpaceForm({ ...spaceForm, sqm_available: e.target.value })} /></div>
                <div><Label>Minimum months</Label><Input data-testid="wh-space-min" type="number" min={1} value={spaceForm.min_months} onChange={(e) => setSpaceForm({ ...spaceForm, min_months: e.target.value })} /></div>
              </div>
              <Button className="mt-3" data-testid="wh-space-submit" onClick={addSpace}><Storefront size={16} className="mr-1.5" /> List the space</Button>
            </div>
          )}
          {spaces.length === 0 ? (
            <p data-testid="wh-space-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No spare space listed yet.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wh-space-list">
              {spaces.map((row, i) => (
                <div key={row.id} data-testid={`wh-space-${i}`} className="border border-border bg-white p-4">
                  <p className="font-semibold text-[#0F172A]">{row.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{row.owner_name} · {row.city || ""} {row.country} · {row.space_type}</p>
                  <p className="mt-2 text-sm text-slate-600">
                    {row.pallets_available ? `${row.pallets_available} pallet space(s) at ${String(row.currency).toUpperCase()} ${row.rate_per_pallet_month}/month` : ""}
                    {row.sqm_available ? ` · ${row.sqm_available} sqm at ${String(row.currency).toUpperCase()} ${row.rate_per_sqm_month}/month` : ""}
                    {` · min ${row.min_months} month(s)`}
                  </p>
                  <div className="mt-3 flex gap-2">
                    {row.owner_id !== user?.id && <Button size="sm" data-testid={`wh-space-book-${i}`} onClick={() => bookSpace(row)}>Request space</Button>}
                    {(user?.role === "admin" || row.owner_id === user?.id) && (
                      <Button size="sm" variant="outline" data-testid={`wh-space-withdraw-${i}`} onClick={async () => {
                        try { await api.delete(`/warehouse/space/${row.id}`); loadSpaces(); toast.success("Withdrawn"); } catch (e) { err(e); }
                      }}>Withdraw</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {bookings.length > 0 && (
            <div className="space-y-2" data-testid="wh-booking-list">
              <p className="text-sm font-semibold text-[#0F172A]">Space bookings</p>
              {bookings.map((b, i) => (
                <div key={b.id} data-testid={`wh-booking-${i}`} className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                  <span className="font-semibold text-[#0F172A]">{b.title}</span>
                  <span className="text-slate-600">{b.pallets || b.sqm} for {b.months} month(s) · {String(b.currency).toUpperCase()} {b.total_cost?.toLocaleString()}</span>
                  <span className="text-xs text-slate-500">{b.booked_by_name} · {b.status}</span>
                  {b.owner_id === user?.id && b.status === "requested" && (
                    <span className="flex gap-2">
                      <Button size="sm" data-testid={`wh-booking-accept-${i}`} onClick={() => answerBooking(b.id, "accepted")}>Accept</Button>
                      <Button size="sm" variant="outline" data-testid={`wh-booking-decline-${i}`} onClick={() => answerBooking(b.id, "declined")}>Decline</Button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "tasks" && (
        <div className="space-y-4" data-testid="wh-tasks">
          {isManager && (
            <div className="border border-border bg-white p-4">
              <p className="text-sm font-semibold text-[#0F172A]">Raise a pick or put-away list</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <div>
                  <Label>Kind</Label>
                  <select data-testid="wh-task-kind" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={taskForm.kind} onChange={(e) => setTaskForm({ ...taskForm, kind: e.target.value })}>
                    <option value="pick">Pick</option>
                    <option value="putaway">Put away</option>
                    <option value="count">Stock count</option>
                  </select>
                </div>
                <div>
                  <Label>Item</Label>
                  <select data-testid="wh-task-item" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={taskForm.item_id} onChange={(e) => setTaskForm({ ...taskForm, item_id: e.target.value })}>
                    <option value="">Pick an item</option>
                    {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <div><Label>Quantity</Label><Input data-testid="wh-task-qty" type="number" min={0} value={taskForm.quantity} onChange={(e) => setTaskForm({ ...taskForm, quantity: e.target.value })} /></div>
                <div>
                  <Label>Assign to</Label>
                  <select data-testid="wh-task-assignee" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={taskForm.assignee_id} onChange={(e) => setTaskForm({ ...taskForm, assignee_id: e.target.value })}>
                    <option value="">Nobody yet</option>
                    {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div><Label>Note</Label><Input data-testid="wh-task-note" value={taskForm.reference_note} onChange={(e) => setTaskForm({ ...taskForm, reference_note: e.target.value })} /></div>
              </div>
              <Button className="mt-3" data-testid="wh-task-submit" onClick={addTask} disabled={!siteId}><ListChecks size={16} className="mr-1.5" /> Raise the task</Button>
            </div>
          )}
          {tasks.length === 0 ? (
            <p data-testid="wh-tasks-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">No tasks on the board.</p>
          ) : tasks.map((t, i) => (
            <div key={t.id} data-testid={`wh-task-${i}`} className="border border-border bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-[#0F172A]">{t.reference} · {t.kind} · {t.site_name}</p>
                <span className="border border-border px-2 py-1 text-xs font-semibold text-slate-600">{t.status.replace(/_/g, " ")}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{t.assignee_name ? `For ${t.assignee_name}` : "Unassigned"}{t.reference_note ? ` · ${t.reference_note}` : ""}</p>
              <ul className="mt-3 space-y-2">
                {t.lines.map((line, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <input data-testid={`wh-task-${i}-line-${idx}`} type="checkbox" checked={!!line.done} onChange={(e) => tickLine(t, idx, e.target.checked)} />
                    <span className={line.done ? "text-slate-400 line-through" : "text-[#0F172A]"}>
                      {line.item_name} × {line.quantity} {line.unit} — <span className="font-semibold">{line.bin_code || "no bin yet"}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!openBin} onOpenChange={(o) => !o && setOpenBin(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Bin {openBin?.code}</DialogTitle></DialogHeader>
          {openBin && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">{openBin.used_units} of {openBin.capacity_units} units used{openBin.fill_pct != null ? ` (${openBin.fill_pct}%)` : ""}</p>
              {openBin.contents.length === 0 ? (
                <p data-testid="wh-bin-empty" className="text-slate-500">This bin is empty.</p>
              ) : (
                <ul className="space-y-1" data-testid="wh-bin-contents">
                  {openBin.contents.map((c) => (
                    <li key={c.item_id} className="flex justify-between border-b border-border pb-1">
                      <span>{c.item_name}</span><span className="font-semibold">{c.quantity} {c.unit}</span>
                    </li>
                  ))}
                </ul>
              )}
              <Button variant="outline" className="w-full" data-testid="wh-bin-move-from" onClick={() => { setMoveForm((f) => ({ ...f, from_bin_code: openBin.code })); setOpenBin(null); setScanOpen(true); }}>
                Move stock out of this bin
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Scan to move stock</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <BinScanner onCode={(code) => setMoveForm((f) => (!f.from_bin_code ? { ...f, from_bin_code: code } : { ...f, to_bin_code: code }))} />
            <div>
              <Label>Item</Label>
              <select data-testid="wh-move-item" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={moveForm.item_id} onChange={(e) => setMoveForm({ ...moveForm, item_id: e.target.value })}>
                <option value="">Pick an item</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div><Label>From bin</Label><Input data-testid="wh-move-from" value={moveForm.from_bin_code} onChange={(e) => setMoveForm({ ...moveForm, from_bin_code: e.target.value.toUpperCase() })} placeholder="A-01-01" /></div>
            <div><Label>To bin</Label><Input data-testid="wh-move-to" value={moveForm.to_bin_code} onChange={(e) => setMoveForm({ ...moveForm, to_bin_code: e.target.value.toUpperCase() })} placeholder="A-02-03" /></div>
            <div><Label>Quantity</Label><Input data-testid="wh-move-qty" type="number" min={0} value={moveForm.quantity} onChange={(e) => setMoveForm({ ...moveForm, quantity: e.target.value })} /></div>
            <Button className="w-full" data-testid="wh-move-submit" onClick={doMove}>Move the stock</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
